import React, { useEffect, useState } from "react";
import { Z } from "../../../theme/zIndex";
import { getNombreSector } from "../../calendar/utils/zonas";
import { isInternalDepartment } from "../../calendar/utils/internalDepartments";
import {
  Box,
  Paper,
  Typography,
  CardContent,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Grid,
  useTheme,
  useMediaQuery,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Alert,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { format, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";
import ReservationVoucher from "./ReservationVoucher";
import AppDialog from "../../shared/components/AppDialog";
import { getReservationIdentityLabel } from "../utils/reservationLabels";
import { useDeleteReservation } from "../../../hooks/useReservationMutations";
import EditIcon from "@mui/icons-material/Edit";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import DeleteIcon from "@mui/icons-material/Delete";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import NoteIcon from "@mui/icons-material/Note";
import { formatCurrency } from "../../shared/utils/formatters";
import { formatTipoCama } from "../../shared/utils/formatters";
import { parseDateOnly } from "../../shared/utils/dateUtils";
import { Reservation } from "../types/Reservation";
import api from "../../../config/axios";
import { fetchDepartmentInfo } from "../../departments/lib/fetchCategories";
import ShareIcon from "@mui/icons-material/Share";
import HistoryIcon from "@mui/icons-material/History";
import { UserPermissions } from "../../auth/hooks/usePermissions";
import { useTheme as useThemeMui } from "@mui/material";
import { useEditRestriction } from "../../auth/hooks/useEditRestriction";
import RestrictionModal from "../../auth/components/RestrictionModal";
import KingBedIcon from "@mui/icons-material/KingBed";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SingleBedIcon from "@mui/icons-material/SingleBed";
import BadgeIcon from "@mui/icons-material/Badge";
import { logger } from "../../../utils/logger";
import {
  LazyClientHistoryModal,
  LazyHistorialDeReservaDialog,
  LazyGodModeEditModal,
} from "../../shared/components/LazyModals";
import { useEstadiasPrevias } from "../hooks/useEstadiasPrevias";
import { tieneDatosDeAfiliado } from "../utils/afiliado";
import EstadiasPreviasChip from "./EstadiasPreviasChip";
import ArcaAvisoCliente from "../../clients/components/ArcaAvisoCliente";
import type { Cliente } from "../../clients/types/Client";
import { useCloseOnBack } from "../../shared/hooks/useCloseOnBack";
import { buildVoucherShareText } from "../utils/voucherShare";

interface ReservationDetailsProps {
  reservation: Reservation | null;
  onClose: () => void;
  onEdit: (id: number) => void;
  onDeleted?: (reservation: Reservation) => void;
  permissions: UserPermissions;
  zIndexBase?: number;
  /** El historial del cliente renderiza este componente adentro. Cuando se abre
   *  desde ahí llega en false, para no ofrecer volver al historial del que se
   *  viene y encadenar capas sin fin. */
  permitirVerHistorial?: boolean;
}

type ApiErrorShape = {
  response?: {
    status?: number;
    data?: {
      error?: string;
    };
  };
};

type ZonePalette = {
  zonas: {
    centro: { border: string };
    loma: { border: string };
  };
};

const getApiError = (error: unknown): ApiErrorShape => {
  return error && typeof error === "object" ? (error as ApiErrorShape) : {};
};

const getErrorName = (error: unknown): string | undefined => {
  return error && typeof error === "object" && "name" in error
    ? String(error.name)
    : undefined;
};

const ReservationDetails: React.FC<ReservationDetailsProps> = ({
  reservation,
  onClose,
  onEdit,
  onDeleted,
  permissions,
  zIndexBase,
  permitirVerHistorial = true,
}: ReservationDetailsProps) => {
  const [historialAbierto, setHistorialAbierto] = useState(false);
  const [actividadAbierta, setActividadAbierta] = useState(false);
  const clienteId = reservation.cliente_id ?? reservation.cliente?.id ?? null;
  const estadiasPrevias = useEstadiasPrevias(
    clienteId,
    reservation.id,
    reservation.entrada,
    reservation.estadias_previas
  );

  /** El modal de historial pide el cliente maestro, pero todos sus campos ya
   *  viajan en la reserva: se arma acá y se evita una consulta extra. El
   *  historial en sí se pide por id, así que es correcto aunque este snapshot
   *  esté desactualizado. */
  const clienteParaHistorial: Cliente | null = clienteId
    ? {
        id: clienteId,
        nombre: reservation.nombre || "",
        apellido: reservation.apellido || "",
        dni: reservation.dni || "",
        email: reservation.email1 || "",
        telefono: reservation.telefono1 || "",
        localidad: reservation.localidad || "",
        origen_reserva:
          (reservation.cliente?.origen_reserva as Cliente["origen_reserva"]) ||
          "directo",
        es_empresa: Boolean(reservation.cliente?.es_empresa),
      }
    : null;

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showVoucher, setShowVoucher] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voucherBlob, setVoucherBlob] = useState<Blob | null>(null);
  const [advancedEditOpen, setAdvancedEditOpen] = useState(false);
  const deleteReservationMutation = useDeleteReservation();
  const detailsZIndex = zIndexBase ?? Z.drawer;
  const nestedOverlayZIndex = zIndexBase ? zIndexBase + 10 : Z.modal;

  // Hook para manejar restricciones de edición
  const {
    isRestrictionModalOpen,
    handleEditAttempt,
    closeRestrictionModal,
    restrictionMessage,
  } = useEditRestriction();

  useCloseOnBack(Boolean(reservation), onClose);

  // Función para manejar el clic en editar
  const handleEditClick = () => {
    const canEdit = handleEditAttempt();
    if (canEdit && reservation) {
      onEdit(reservation.id);
    }
    // Si no puede editar, el modal se abrirá automáticamente
  };

  // Verificar que reservation existe antes de continuar
  if (!reservation) {
    return null;
  }

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const themeMui = useThemeMui();

  // Usar valor_total (precio actual) en lugar de valor_original (precio histórico)
  const valorTotal = Number(reservation.valor_total) || 0;
  const isInternalReservation = isInternalDepartment(reservation.departamento);
  const recargoLateCheckout = Number(reservation.recargo_late_checkout) || 0;
  const totalSinLateCheckout =
    Number(reservation.total_sin_late_checkout) ||
    Math.max(0, valorTotal - recargoLateCheckout);
  const saldoPendiente = valorTotal - (Number(reservation.pago) || 0);
  const saldoLabel =
    saldoPendiente > 0
      ? formatCurrency(saldoPendiente)
      : saldoPendiente < 0
      ? `A favor ${formatCurrency(Math.abs(saldoPendiente))}`
      : "Saldada";

  const handleDelete = async () => {
    if (!reservation || loading || deleteReservationMutation.isPending) return;
    setLoading(true);
    setError(null);

    try {
      logger.debug("Intentando eliminar reserva:", reservation.id);

      // Usar la mutación en lugar de api.delete directamente
      await deleteReservationMutation.mutateAsync(reservation.id);

      // Si llegamos aquí, la eliminación fue exitosa
      onDeleted?.(reservation);
      onClose();
    } catch (e: unknown) {
      logger.error("Error al eliminar la reserva:", e);
      const apiError = getApiError(e);

      // Si el error es 404, significa que la reserva ya no existe
      // Esto puede pasar si el WebSocket ya notificó la eliminación
      if (apiError.response?.status === 404) {
        logger.debug(
          "Reserva ya eliminada (404), considerando operación exitosa"
        );
        // Si la reserva ya no existe, consideramos la operación como exitosa
        onDeleted?.(reservation);
        onClose();
      } else {
        // Para otros errores, mostramos el mensaje correspondiente
        setError(
          apiError.response?.data?.error || "Error al eliminar la reserva"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const fechaEntrada = parseDateOnly(reservation.entrada);
  const fechaSalida = parseDateOnly(reservation.salida);
  const cantidadNoches = differenceInDays(fechaSalida, fechaEntrada);
  const precioPorNocheCobrado =
    Number(reservation.precio_por_noche_cobrado) ||
    (cantidadNoches > 0 ? totalSinLateCheckout / cantidadNoches : 0);
  const zonaColor =
    parseInt(reservation.departamento) <= 18
      ? (themeMui.palette as typeof themeMui.palette & ZonePalette).zonas.centro
          .border
      : (themeMui.palette as typeof themeMui.palette & ZonePalette).zonas.loma
          .border;

  const sanitizeFilePart = (value: string): string => {
    const normalized = value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return normalized || "Cliente";
  };

  const formatFileDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    let isActive = true;
    const preloadVoucherBlob = async () => {
      if (!reservation) {
        setVoucherBlob(null);
        return;
      }
      try {
        const response = await api.get(`/reservas/${reservation.id}/voucher`, {
          responseType: "blob",
        });
        if (isActive) {
          setVoucherBlob(response.data);
        }
      } catch (e) {
        if (isActive) {
          setVoucherBlob(null);
        }
        logger.warn("No se pudo precargar el voucher para compartir:", e);
      }
    };

    preloadVoucherBlob();
    return () => {
      isActive = false;
    };
  }, [reservation?.id]);

  const handleShareVoucher = async () => {
    if (!reservation) return;
    setLoading(true);
    setError(null);
    try {
      logger.debug("Preparando voucher PDF para compartir:", reservation.id);
      const [pdfBlob, departmentInfo] = await Promise.all([
        voucherBlob
          ? Promise.resolve(voucherBlob)
          : api
              .get(`/reservas/${reservation.id}/voucher`, {
                responseType: "blob",
              })
              .then((response) => response.data),
        fetchDepartmentInfo(String(reservation.departamento)).catch((e) => {
          logger.error("No se pudo cargar la ubicación para compartir:", e);
          return null;
        }),
      ]);

      const apellidoArchivo = sanitizeFilePart(reservation.apellido || "Cliente");
      const deptoArchivo = sanitizeFilePart(String(reservation.departamento || "Depto"));
      const fechaArchivo = formatFileDate(new Date());
      const voucherFileName = `R${reservation.id}_Depto_${deptoArchivo}_${apellidoArchivo}_${fechaArchivo}.pdf`;

      const file = new File(
        [pdfBlob],
        voucherFileName,
        {
          type: "application/pdf",
        }
      );

      const shareTitle = "Confirmación de estadía";
      const shareText = buildVoucherShareText(
        reservation,
        departmentInfo?.ubicacion
      );
      let canShareFiles = false;
      const shareDiagnostics: Record<string, unknown> = {
        reservationId: reservation.id,
        departmentAddress: departmentInfo?.ubicacion ?? null,
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        platform:
          typeof navigator !== "undefined" ? navigator.platform : "unknown",
        hasNavigatorShare:
          typeof navigator !== "undefined" &&
          typeof navigator.share === "function",
        hasNavigatorCanShare:
          typeof navigator !== "undefined" &&
          typeof navigator.canShare === "function",
        pdfBlobSize: file.size,
      };
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.share === "function" &&
        typeof navigator.canShare === "function"
      ) {
        try {
          canShareFiles = navigator.canShare({ files: [file] });
          shareDiagnostics.canShareFilesResult = canShareFiles;
        } catch (e) {
          logger.warn("El navegador no pudo validar canShare(files):", e);
          shareDiagnostics.canShareFilesError =
            e instanceof Error ? e.message : String(e);
          canShareFiles = false;
        }
      }

      // Prioridad: compartir archivo cuando el navegador realmente lo soporta.
      if (canShareFiles) {
        logger.info("Intentando compartir voucher como archivo", shareDiagnostics);
        try {
          await navigator.share({
            files: [file],
            title: shareTitle,
            text: shareText,
          });
          return;
        } catch (e) {
          if ((e as { name?: string })?.name === "AbortError") {
            logger.info("Compartir voucher cancelado por el usuario");
            return;
          }
          logger.warn(
            "Fallo al compartir PDF como archivo. Se descargará voucher.",
            e
          );
        }
      }

      logger.info(
        "No hay soporte para compartir PDF como archivo. Se descarga voucher.",
        shareDiagnostics
      );
      // Fallback único: descargar PDF para envío manual.
      const url = window.URL.createObjectURL(file);
      try {
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file.name);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally {
        window.URL.revokeObjectURL(url);
      }
      setError(
        "No se pudo compartir el PDF en este dispositivo. Descargamos el voucher para que puedas enviarlo manualmente."
      );
    } catch (e: unknown) {
      if (getErrorName(e) === "AbortError") {
        logger.info("Compartir voucher cancelado por el usuario");
        return;
      }
      logger.error("Error al compartir voucher:", {
        errorName: e?.name,
        errorMessage: e?.message,
        reservationId: reservation.id,
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        hasNavigatorShare:
          typeof navigator !== "undefined" &&
          typeof navigator.share === "function",
        hasNavigatorCanShare:
          typeof navigator !== "undefined" &&
          typeof navigator.canShare === "function",
      });
      const technicalReason =
        e?.name || e?.message
          ? ` (${e?.name ?? "Error"}${e?.message ? `: ${e.message}` : ""})`
          : "";
      setError(
        `No se pudo abrir el menú de compartir en este dispositivo. Probá descargar el PDF y enviarlo manualmente por WhatsApp.${technicalReason}`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={!!reservation}
        onClose={onClose}
        sx={{
          zIndex: detailsZIndex,
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 400 },
            maxWidth: "100vw",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: { xs: 1, sm: 2 },
            pt: { xs: 0.5, sm: 2 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="h6"
              sx={{
                fontSize: "1rem",
                fontWeight: 700,
                color: "text.primary",
              }}
            >
              Reserva #{reservation.id}
            </Typography>
            <Chip
              label={
                reservation.estado
                  ? reservation.estado.toUpperCase()
                  : "CONFIRMADA"
              }
              size="small"
              color={
                reservation.estado === "confirmada" ? "success" : "warning"
              }
            />
            {reservation.huesped_info_status === "faltante" && (
              <Tooltip
                title={reservation.huesped_info_motivo || "Falta huésped o afiliado"}
                arrow
              >
                <Chip
                  label="Falta huésped"
                  size="small"
                  color="error"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Contenido principal */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            minHeight: 0,
            p: { xs: 1, sm: 2 },
          }}
        >
          {/* Info de departamento */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight={500}>
              Depto {reservation.departamento}
            </Typography>
            <Chip
              label={getNombreSector(reservation.departamento)}
              size="small"
              sx={{
                bgcolor: zonaColor,
                color: "#ffffff",
                fontWeight: 600,
                "& .MuiChip-label": {
                  color: "#ffffff",
                },
              }}
            />
          </Stack>

          {/* Fechas */}
          <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarTodayIcon fontSize="small" color="primary" />
                <Typography variant="body2">
                  {format(fechaEntrada, "dd/MM/yyyy", { locale: es })} -{" "}
                  {format(fechaSalida, "dd/MM/yyyy", { locale: es })}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {cantidadNoches} {cantidadNoches === 1 ? "noche" : "noches"}
              </Typography>
            </Stack>
          </CardContent>

          <Divider sx={{ my: 1 }} />

          {/* Titular de la reserva */}
          <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonIcon fontSize="small" color="primary" />
                <Typography variant="body2">
                  {reservation.nombre} {reservation.apellido}
                </Typography>
              </Stack>
              {clienteId ? (
                <Box sx={{ pl: 3.5, mt: -0.5 }}>
                  <EstadiasPreviasChip
                    datos={estadiasPrevias}
                    onVerHistorial={
                      permitirVerHistorial && clienteParaHistorial
                        ? () => setHistorialAbierto(true)
                        : undefined
                    }
                  />
                </Box>
              ) : null}
              {reservation.dni && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <BadgeIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    {getReservationIdentityLabel(
                      reservation.cliente?.origen_reserva,
                      true
                    )}{" "}
                    {reservation.dni}
                  </Typography>
                </Stack>
              )}
              {reservation.cliente && <ArcaAvisoCliente cliente={reservation.cliente} />}
              {reservation.telefono1 && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <PhoneIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    {reservation.telefono1}
                  </Typography>
                </Stack>
              )}
              {reservation.email1 && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <EmailIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    {reservation.email1}
                  </Typography>
                </Stack>
              )}
              {reservation.localidad && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocationOnIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    {reservation.localidad}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </CardContent>

          <Divider sx={{ my: 1 }} />

          {/* Datos del afiliado */}
          {tieneDatosDeAfiliado(reservation) && (
            <>
              <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PersonIcon fontSize="small" color="primary" />
                    <Typography variant="body2" fontWeight={500}>
                      Afiliado
                    </Typography>
                  </Stack>
                  {reservation.afiliado_nombre?.trim() && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">
                        <strong>Nombre:</strong> {reservation.afiliado_nombre}
                      </Typography>
                    </Stack>
                  )}
                  {reservation.afiliado_apellido?.trim() && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">
                        <strong>Apellido:</strong> {reservation.afiliado_apellido}
                      </Typography>
                    </Stack>
                  )}
                  {reservation.afiliado_dni && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">
                        <strong>DNI:</strong> {reservation.afiliado_dni}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {/* Tipo de cama */}
          <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
            <Stack direction="row" spacing={1} alignItems="center">
              {reservation.tipo_cama &&
              reservation.tipo_cama.includes("doble") ? (
                <KingBedIcon fontSize="small" color="primary" />
              ) : (
                <SingleBedIcon fontSize="small" color="primary" />
              )}
              <Typography variant="body2">
                {formatTipoCama(reservation.tipo_cama)}
              </Typography>
            </Stack>
          </CardContent>

          <Divider sx={{ my: 1 }} />

          {/* Notas */}
          {reservation.notas && (
            <>
              <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <NoteIcon fontSize="small" color="primary" />
                    <Typography variant="body2" fontWeight={500}>
                      Notas
                    </Typography>
                  </Stack>
                  <Paper variant="outlined" sx={{ p: 1, bgcolor: "grey.50" }}>
                    <Typography variant="body2" color="text.secondary">
                      {reservation.notas}
                    </Typography>
                  </Paper>
                </Stack>
              </CardContent>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {/* Acompañantes */}
          {reservation.acompanantes &&
            Array.isArray(reservation.acompanantes) &&
            reservation.acompanantes.length > 0 && (
              <Accordion
                elevation={0}
                sx={{
                  "&:before": { display: "none" },
                  bgcolor: "transparent",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon fontSize="small" />}
                  sx={{ p: 0, minHeight: 40 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PersonIcon fontSize="small" color="primary" />
                    <Typography variant="body2" fontWeight={500}>
                      Acompañantes ({reservation.acompanantes.length})
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1 }}>
                  <List dense disablePadding>
                    {reservation.acompanantes.map((acompanante, index) => (
                      <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              {acompanante.nombre} {acompanante.apellido}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              DNI: {acompanante.dni} • Edad: {acompanante.edad}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

          {/* Información de pago */}
          {!isInternalReservation && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                bgcolor: theme.palette.background.paper,
                borderColor: theme.palette.divider,
                mt: 1,
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">Valor Total:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(valorTotal)}
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">
                    {reservation.late_checkout ? "Precio por noche:" : "Promedio por noche:"}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(precioPorNocheCobrado)}
                  </Typography>
                </Stack>

                {reservation.late_checkout && recargoLateCheckout > 0 && (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      Late checkout:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(recargoLateCheckout)}
                    </Typography>
                  </Stack>
                )}

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">Pagado:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(reservation.pago || 0)}
                  </Typography>
                </Stack>

                <Divider sx={{ my: 0.5 }} />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">Saldo:</Typography>
                  <Chip
                    label={saldoLabel}
                    color={saldoPendiente > 0 ? "warning" : "success"}
                    variant={saldoPendiente > 0 ? "filled" : "outlined"}
                    size="small"
                  />
                </Stack>

                {/* Ajuste financiero si existe */}
                {reservation.tipo_ajuste &&
                  reservation.tipo_ajuste !== "ninguno" &&
                  (Number(reservation.pago) || 0) >
                    (Number(reservation.valor_total) || 0) && (
                    <>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          bgcolor:
                            reservation.tipo_ajuste === "sobrepago"
                              ? "warning.50"
                              : "success.50",
                          border: "1px solid",
                          borderColor:
                            reservation.tipo_ajuste === "sobrepago"
                              ? "warning.200"
                              : "success.200",
                          mt: 0.5,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography
                            variant="body2"
                            color={
                              reservation.tipo_ajuste === "sobrepago"
                                ? "warning.dark"
                                : "success.dark"
                            }
                            sx={{ fontWeight: 500 }}
                          >
                            Dinero a favor
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography
                            variant="body2"
                            color={
                              reservation.tipo_ajuste === "sobrepago"
                                ? "warning.dark"
                                : "success.dark"
                            }
                            sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                          >
                            {reservation.tipo_ajuste === "sobrepago"
                              ? "Retenido"
                              : "Devuelto"}
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={
                              reservation.tipo_ajuste === "sobrepago"
                                ? "warning.dark"
                                : "success.dark"
                            }
                          >
                            {formatCurrency(
                              Math.max(
                                0,
                                (Number(reservation.pago) || 0) -
                                  (Number(reservation.valor_total) || 0)
                              )
                            )}
                          </Typography>
                        </Stack>
                      </Stack>
                    </>
                  )}
              </Stack>
            </Paper>
          )}
        </Box>

        {/* Botones de acción */}
        <Box
          sx={{
            p: { xs: 1, sm: 2 },
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant={isMobile ? "outlined" : "contained"}
                startIcon={<ConfirmationNumberIcon fontSize="small" />}
                onClick={() => setShowVoucher(true)}
                size="small"
                sx={{ minHeight: 40 }}
              >
                Voucher
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant={isMobile ? "outlined" : "contained"}
                startIcon={<ShareIcon fontSize="small" />}
                onClick={handleShareVoucher}
                disabled={loading}
                size="small"
                sx={{ minHeight: 40 }}
              >
                Compartir
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant={isMobile ? "outlined" : "contained"}
                startIcon={<EditIcon fontSize="small" />}
                onClick={handleEditClick}
                size="small"
                sx={{ minHeight: 40 }}
              >
                Editar
              </Button>
            </Grid>

            {permissions.isAdmin && (
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  startIcon={<HistoryIcon fontSize="small" />}
                  onClick={() => setActividadAbierta(true)}
                  size="small"
                  sx={{
                    minHeight: 40,
                    borderColor: "divider",
                    color: "text.secondary",
                    "&:hover": {
                      borderColor: "text.secondary",
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  Historial
                </Button>
              </Grid>
            )}

            {permissions.isSuperAdmin && (
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  startIcon={<AdminPanelSettingsIcon fontSize="small" />}
                  onClick={() => setAdvancedEditOpen(true)}
                  size="small"
                  sx={{
                    minHeight: 40,
                    borderColor: "divider",
                    color: "text.secondary",
                    "&:hover": {
                      borderColor: "text.secondary",
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  Avanzada
                </Button>
              </Grid>
            )}

            {permissions.canDeleteReservations && (
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant={isMobile ? "outlined" : "contained"}
                  color="error"
                  startIcon={<DeleteIcon fontSize="small" />}
                  onClick={() => setConfirmDelete(true)}
                  size="small"
                  sx={{ minHeight: 40 }}
                >
                  Eliminar
                </Button>
              </Grid>
            )}
          </Grid>

          {error && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}

        </Box>
      </Drawer>

      {permissions.isSuperAdmin && advancedEditOpen && (
        <LazyGodModeEditModal
          open={advancedEditOpen}
          onClose={() => setAdvancedEditOpen(false)}
          onSuccess={() => {
            setAdvancedEditOpen(false);
          }}
          reservation={reservation}
          zIndex={nestedOverlayZIndex}
        />
      )}

      {historialAbierto && clienteParaHistorial && (
        <LazyClientHistoryModal
          open={historialAbierto}
          onClose={() => setHistorialAbierto(false)}
          cliente={clienteParaHistorial}
          zIndexBase={nestedOverlayZIndex}
        />
      )}

      {actividadAbierta && (
        <LazyHistorialDeReservaDialog
          open={actividadAbierta}
          onClose={() => setActividadAbierta(false)}
          reservaId={reservation.id}
          subtitulo={[
            [reservation.nombre, reservation.apellido].filter(Boolean).join(" "),
            reservation.departamento ? `Depto ${reservation.departamento}` : "",
          ]
            .filter(Boolean)
            .join(" · ")}
          zIndex={nestedOverlayZIndex}
        />
      )}

      {/* Diálogo de confirmación de eliminación */}
      <AppDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        sx={{ zIndex: nestedOverlayZIndex }}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Confirmar eliminación
          </Typography>
          <Typography sx={{ mb: 3 }}>
            ¿Estás seguro de que deseas eliminar la reserva de{" "}
            {reservation.nombre} {reservation.apellido} (Depto{" "}
            {reservation.departamento})? Esta acción no se puede deshacer.
          </Typography>

          {/* Mostrar error si hay */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setConfirmDelete(false)}>Cancelar</Button>
            <Button
              onClick={handleDelete}
              color="error"
              variant="contained"
              disabled={loading}
            >
              {loading ? "Eliminando..." : "Eliminar"}
            </Button>
          </Box>
        </Box>
      </AppDialog>

      {/* Diálogo del voucher */}
      <AppDialog
        open={showVoucher}
        onClose={() => setShowVoucher(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          zIndex: nestedOverlayZIndex,
        }}
        PaperProps={{
          sx: {
            bgcolor: "transparent",
            boxShadow: "none",
            overflow: "visible",
            maxHeight: "none",
            height: "auto",
            m: 0,
            p: 0,
          },
        }}
        BackdropProps={{
          sx: {
            bgcolor: "rgba(0,0,0,0.3)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <ReservationVoucher
          reserva={reservation}
          onClose={() => setShowVoucher(false)}
        />
      </AppDialog>

      {/* Modal de restricción de edición */}
      <RestrictionModal
        open={isRestrictionModalOpen}
        onClose={closeRestrictionModal}
        message={restrictionMessage}
        contactPerson="Mariela Mendes Nunes"
        zIndex={nestedOverlayZIndex}
      />
    </>
  );
};

export default ReservationDetails;
